import React from 'react'
import { TRACK } from '@disco/constants/ItemTypes'
import { cancelExplorerUpload } from '@disco/state-management/actions/upload-actions'
import { getExplorerUpload } from '@disco/state-management/selectors/explorer'
import { tempItemToTrackRow } from '@disco/utils/tracks'
import type { ExplorerPane } from 'types/explorer'
import type { State } from 'types/redux'
import TrackItem from 'components/common/track-item'
import Button from 'components/ui/button'
import { HoverProvider } from 'components/ui/hover-provider'
import { useDispatch, useSelector } from 'hooks/use-redux'

type Props = {
  tempId: string
  pane: ExplorerPane
}

/**
 * In-pane row for a file uploading into this pane's group: filename with a
 * progress bar while uploading, then "Processing…" until the finished track
 * row takes its place. The shared TrackItem renders all upload states for
 * id-less track-shaped objects, same as the Playlist Creator's temp rows.
 */
const PaneUploadItem = ({ tempId, pane }: Props) => {
  const dispatch = useDispatch()
  const upload = useSelector((state: State) => getExplorerUpload(state, tempId))

  if (!upload) return null

  // Once the upload finalized and pane reconciliation appended the real
  // track row, yield to it — render-time check, immune to dispatch ordering.
  const realRowArrived =
    upload.trackId !== undefined &&
    (pane.results ?? []).some(
      item => item.type === TRACK && item.id === upload.trackId,
    )
  if (realRowArrived) return null

  return (
    // Same wrapper every other pane row uses: PaneItem reads isHover from the
    // context to reveal its actions, so without a provider the cancel button
    // would stay hidden on desktop.
    <HoverProvider enableTouchSupport>
      <TrackItem
        location="explorer"
        track={tempItemToTrackRow(upload)}
        actions={
          !upload.trackId && (
            <Button
              appearance="icon"
              compact
              icon="clear"
              tooltip="Cancel upload"
              onClick={() => dispatch(cancelExplorerUpload(tempId))}
            />
          )
        }
      />
    </HoverProvider>
  )
}

export default React.memo<Props>(PaneUploadItem)
